import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { Breadcrumbs } from '../../components/guide/Breadcrumbs';
import { FaqList } from '../../components/guide/FaqList';
import { getCategory, visibleCategories, entriesInCategory, entryPath, entryKindLabel, guideFaqs, FAQ_CATEGORY_ID } from '../../utils/guideIndex';
import type { GuideEntry } from '../../types/guide';
function groupByKind(entries: GuideEntry[]) {
  const groups = new Map<string, GuideEntry[]>();
  entries.forEach(e => {
    const k = entryKindLabel(e);
    groups.set(k, [...(groups.get(k) ?? []), e]);
  });
  return Array.from(groups.entries());
}
export function GuideCategoryPage() {
  const {
    categoryId = ''
  } = useParams();
  const cat = getCategory(categoryId);
  const visible = !!cat && visibleCategories.some(c => c.id === cat.id);
  useEffect(() => {
    if (cat) document.title = `${cat.title} · Guía MU Vanguard`;
  }, [cat]);
  if (!cat || !visible) return <Navigate to="/guia" replace data-id="element-2102" data-mp="253eftxjsvi7q" />;
  const Icon = cat.icon;
  const entries = entriesInCategory(cat.id);
  const featured = entries.filter(e => e.kind === 'article');
  const others = entries.filter(e => e.kind !== 'article');
  const isFaq = cat.id === FAQ_CATEGORY_ID;
  return <div data-id="element-2103" data-mp="2k28gjn02u9hd">
      <Breadcrumbs items={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: cat.title
    }]} data-id="element-2104" data-mp="2k1ymnobwvi6e" />
      <header className="flex items-start gap-4" data-id="element-2105" data-mp="1megi6ppa9287">
        <span className="flex h-12 w-12 md:h-14 md:w-14 shrink-0 items-center justify-center rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]" data-id="element-2106" data-mp="1mfrk0jz63zwo">
          <Icon className="h-6 w-6" aria-hidden="true" data-id="element-2107" data-mp="1wb31ha1ynl84" />
        </span>
        <div data-id="element-2108" data-mp="212ksp6e6m7fw">
          <h1 className="font-cinzel text-white text-2xl sm:text-3xl md:text-[2.5rem] font-black leading-tight" data-id="element-2109" data-mp="2132wco36bnxu">{cat.title}</h1>
          <p className="mt-2 text-[15px] md:text-lg text-gray-300" data-id="element-2110" data-mp="13mnlolb08yvq">{cat.description}</p>
        </div>
      </header>

      {featured.length > 0 && <div className="mt-8 grid gap-4" data-id="element-2111" data-mp="10lkjszldgdfi">
          {featured.map(e => <Link key={e.id} to={entryPath(e)} className="group relative flex min-h-[220px] flex-col justify-end overflow-hidden rounded-3xl border border-[#D4AF37]/40 bg-[#0A1628]" data-id="element-2112" data-mp="10jrdxmil1x8k">
              {e.image && <img src={e.image} alt="" className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-2113" data-mp="15dls6aff5k09" />}
              <div className="absolute inset-0 bg-[#050b14]/60" aria-hidden="true" data-id="element-2114" data-mp="coeufdnsgyg2" />
              <div className="relative p-6 md:p-8" data-id="element-2115" data-mp="rl5xbwwo8saf">
                <span className="text-xs font-semibold text-[#D4AF37]" data-id="element-2116" data-mp="rmqrhlf2q97r">{entryKindLabel(e)}</span>
                <h2 className="mt-1 font-cinzel text-2xl md:text-3xl font-black text-white" data-id="element-2117" data-mp="ml9onkd14coj">{e.title}</h2>
                <p className="mt-2 max-w-xl text-[15px] text-gray-300" data-id="element-2118" data-mp="22yrkxdmvultu">{e.summary}</p>
                <span className="mt-4 inline-flex items-center gap-2 font-cinzel text-sm font-bold text-[#D4AF37]" data-id="element-2119" data-mp="1y0g94xd1218z">
                  Leer la guía completa
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-2120" data-mp="2ajkakxh1ovko" />
                </span>
              </div>
            </Link>)}
        </div>}

      {groupByKind(others).map(([kind, list]) => <section key={kind} className="mt-10" aria-label={kind} data-id="element-2121" data-mp="20nh7npedcswo">
          <h2 className="mb-3 text-sm font-semibold text-gray-400" data-id="element-2122" data-mp="2obs5sksokeh9">{kind} <span className="text-gray-600" data-id="element-2123" data-mp="2tauxl3jngo7a">· {list.length}</span></h2>
          <ul className="divide-y divide-white/10 overflow-hidden rounded-3xl border border-white/10 bg-[#0A1628]" data-id="element-2124" data-mp="2tc61r4h0bxyx">
            {list.map(e => <li key={e.id} data-id="element-2125" data-mp="2jjgvqp487caz">
                <Link to={entryPath(e)} className="group flex items-center gap-4 px-5 py-4 transition-colors duration-150 hover:bg-white/[0.03]" data-id="element-2126" data-mp="2jifgs9k3a5nz">
                  <span className="min-w-0 flex-1" data-id="element-2127" data-mp="3c1clmutjevmb">
                    <span className="flex flex-wrap items-center gap-2" data-id="element-2128" data-mp="3c2xiws78ksnv">
                      <span className="font-cinzel font-bold text-white" data-id="element-2129" data-mp="1odasntj1g221">{e.title}</span>
                      {e.status === 'announced' && <span className="rounded-full bg-[#4A9EFF]/15 px-2 py-0.5 text-[11px] font-bold text-[#4A9EFF]" data-id="element-2130" data-mp="1jc3lnwopz6wf">Anunciado</span>}
                    </span>
                    <span className="mt-0.5 block text-sm text-gray-400" data-id="element-2131" data-mp="1adi341j8zrvn">{e.summary}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-gray-500 transition-colors duration-150 group-hover:text-[#D4AF37]" aria-hidden="true" data-id="element-2132" data-mp="271o6kl4foe8c" />
                </Link>
              </li>)}
          </ul>
        </section>)}

      {isFaq && guideFaqs.length > 0 && <section className="mt-10" aria-labelledby="faq-all-t" data-id="element-2133" data-mp="1tajf46b7ys93">
          <h2 id="faq-all-t" className="mb-2 text-sm font-semibold text-gray-400" data-id="element-2134" data-mp="2pt8l4j7qem2r">Preguntas frecuentes <span className="text-gray-600" data-id="element-2135" data-mp="2l2s993jk78ii">· {guideFaqs.length}</span></h2>
          <FaqList faqs={guideFaqs} linkable data-id="element-2136" data-mp="2l1h0sqf65cl2" />
        </section>}

      {cat.comingSoon && entries.length === 0 && !isFaq && <div className="mt-10 flex items-start gap-3 rounded-3xl border border-dashed border-white/15 p-6" data-id="element-2137" data-mp="cixn5arsgt5l">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-[#4A9EFF]" aria-hidden="true" data-id="element-2138" data-mp="gkau5o6mb2e3" />
          <div data-id="element-2139" data-mp="ljniyeh1ujjb">
            <p className="font-cinzel font-bold text-white" data-id="element-2140" data-mp="lk5ltty7vw07">Próximamente</p>
            <p className="mt-1 text-sm text-gray-400" data-id="element-2141" data-mp="qj8gckgkmjtx">Publicaremos esta sección cuando el sistema esté listo.</p>
          </div>
        </div>}
    </div>;
}